/* eslint-disable react/no-array-index-key */
import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { UserData } from 'react-oidc';
import { v4 as uuid } from 'uuid';
import { getPrompt, getCitizenPrompt, IPrompt } from '../../api-service/clients/ai-search-client';
import { CreateUserChatMessageDto, Vote } from '../../api-service/clients';
import { moduleClientFactory } from '../../api-service/module-client-factory';
import { formatDateTime, generateGuid } from '../../utilities';
import { AssistantType } from '../../components/chat/assistant-type';

export interface IChatMessage {
  content: string;
  role?: 'User' | 'Assistent' | 'Tool' | 'Function' | 'System';
  citations?: ICitation[];
  chatCorrelationId?: string; // Correlates User and Assistent messages
  vote?: Vote;
  qualityComment?: string;
  timestamp?: string;
  userName?: string;
  sessionId?: string;
  isPreparingReply?: boolean; // Indicates if the message is still being generated
  preparationStatusMessage?: string; // Latest update from function calls or similar work being done before the reply strea starts
  isDraftStandardQuestion?: boolean;
  previewModelName?: string;
}

export interface ISummaPreview {
  model: string;
  displayName: string;
}

// Mirrors the system codes the BFF and AiSearch refuse a Summa preview with.
export const summaPreviewRefusalCodes = ['SummaPreviewUnavailable', 'SummaConversationModelChanged', 'SummaPreviewNotAllowed'] as const;

export type SummaPreviewRefusalCode = (typeof summaPreviewRefusalCodes)[number];

export interface ISummaPreviewRefusal {
  systemCode: SummaPreviewRefusalCode;
  question: string;
}

async function readSummaPreviewRefusalCode(response: Response): Promise<SummaPreviewRefusalCode | undefined> {
  const problem = await response.json().catch(() => undefined);
  return summaPreviewRefusalCodes.find(code => code === problem?.systemCode);
}

export interface ICitation {
  link: string;
  text: string;
  title: string;
  id: string;
  CitationSourceType: number;
  isAlternativeResult?: boolean;
  linkDisabled?: boolean;
}

export enum CitationSourceType {
  FasitGuides = 0,
  LegalDocuments = 2,
  MunicipalityAssistantDocuments = 3,
}

const unfinishedReplyText = 'Svaret kunne ikke hentes færdigt. Prøv at stille spørgsmålet igen.';

// The persisted chat carries the scope it belongs to, so a chat stored for one
// citizen can never be restored while another citizen is being viewed.
interface IStoredChat {
  scopeId?: string;
  sessionId?: string;
  messages: IChatMessage[];
  preview?: ISummaPreview;
}

function readStoredChat(storageKey: string): IStoredChat | undefined {
  const stored = sessionStorage.getItem(storageKey);
  if (!stored) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(stored);
    // Chats persisted before the scope was introduced are plain arrays and
    // carry no identity, so they cannot be trusted and are discarded.
    return Array.isArray(parsed?.messages) ? (parsed as IStoredChat) : undefined;
  } catch (e: any) {
    console.error('Error parsing stored chat:', e, stored);
    return undefined;
  }
}

export const useChat = (assistantType: AssistantType, scopeId?: string, chosenPreview?: ISummaPreview) => {
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [citationsTypes, setCitationsTypes] = useState<CitationSourceType[]>([CitationSourceType.FasitGuides]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const context = useContext(UserData);
  const [sessionId, setSessionId] = useState<string | undefined>('');
  const [preview, setPreview] = useState<ISummaPreview | undefined>(undefined);
  const [previewRefusal, setPreviewRefusal] = useState<ISummaPreviewRefusal | undefined>(undefined);
  const conversationStarted = messages.some(message => message.role === 'User');
  // A conversation not yet started follows the superuser's remembered choice, but a refused one stays on the municipality's model.
  const activePreview = preview ?? (conversationStarted || previewRefusal ? undefined : chosenPreview);

  const userDisplayName = context?.user?.profile?.name ?? 'Ukendt bruger';
  const chatMessageStorageKey = `FASIT_${assistantType}_CHATMESSAGE_KEY`;
  const citationTypesStorageKey = `FASIT_${assistantType}_CITATIONTYPES_KEY`;

  // The citizen assistant is meaningless without a citizen, and a chat stored
  // with no scope would match any other scopeless mount. Summa can be mounted
  // without a citizen (chat.tsx renders the panel on tab index alone), so
  // neither read nor write anything in that state.
  const isScopeUsable = (scope?: string) => assistantType !== AssistantType.Citizen || scope !== undefined;

  // The scope the messages in state belong to, which is not necessarily the
  // scope being viewed. Persisting must use this, not scopeId, or a scope
  // change would restamp the previous scope's messages as the new one's.
  // It does not help while a reply is still streaming after a scope change:
  // the load effect has already moved the ref on, so that case stays open.
  const messagesScope = useRef(scopeId);

  // A preview chosen before the first question is stored too, so switching tabs does not quietly turn it off.
  useEffect(() => {
    if ((messages.length > 1 || preview) && isScopeUsable(messagesScope.current)) {
      const storedChat: IStoredChat = { scopeId: messagesScope.current, sessionId, messages, preview };
      sessionStorage.setItem(chatMessageStorageKey, JSON.stringify(storedChat));
    }
  }, [messages, sessionId, preview, chatMessageStorageKey]);

  useEffect(() => {
    const storedChat = readStoredChat(chatMessageStorageKey);
    messagesScope.current = scopeId;
    setPreviewRefusal(undefined);

    if (storedChat && isScopeUsable(scopeId) && storedChat.scopeId === scopeId) {
      setSessionId(storedChat.sessionId);
      setMessages(storedChat.messages);
      setPreview(storedChat.preview);
      return;
    }

    // Unconditional: a pre-scope chat and unparseable JSON both read back as
    // undefined, and leaving them behind means re-reading them on every mount
    // and copying them into every new tab.
    sessionStorage.removeItem(chatMessageStorageKey);
    setSessionId(undefined);
    setMessages([]);
    setPreview(undefined);
  }, [scopeId, chatMessageStorageKey]);

  useEffect(() => {
    const citationTypesStr = localStorage.getItem(citationTypesStorageKey);
    try {
      if (citationTypesStr) {
        setCitationsTypes(JSON.parse(citationTypesStr));
      }
    } catch (e: any) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(citationTypesStorageKey, JSON.stringify(citationsTypes));
  }, [citationsTypes]);

  // Ending a conversation drops its preview, so the next one starts on whatever the superuser has chosen by then.
  const clearChat = useCallback(() => {
    setSessionId(undefined);
    setMessages([{ content: '' }]);
    setPreview(undefined);
    setPreviewRefusal(undefined);
    sessionStorage.removeItem(chatMessageStorageKey);
  }, [chatMessageStorageKey]);

  // The only way to switch model, so a switch always starts a new conversation.
  function startNewConversation(nextPreview?: ISummaPreview) {
    clearChat();
    setPreview(nextPreview);
  }

  const getSessionId = () => {
    if (!sessionId) {
      const newSessionId = generateGuid();
      setSessionId(newSessionId);
      return newSessionId;
    }

    return sessionId;
  };

  // The draft marking only shows in the conversation, so it is kept out of the request sent to Summa.
  const handlePrompt = async (promptData: IPrompt, isDraftStandardQuestion?: boolean) => {
    const prompt = { ...promptData, sessionId: getSessionId(), ...(activePreview && { previewModel: activePreview.model }) };
    // Locked to the conversation, so a choice changed later only reaches the next one.
    setPreview(activePreview);
    const askedInScope = messagesScope.current;
    setIsLoading(true);
    error && setError(undefined);
    setPreviewRefusal(undefined);
    const chatCorrelationId = uuid();
    prompt &&
      prompt.content &&
      setMessages(prev => [
        ...prev,
        {
          content: prompt.content || '',
          role: 'User',
          chatCorrelationId,
          timestamp: formatDateTime(new Date().toISOString()),
          userName: userDisplayName,
          sessionId: prompt.sessionId,
          isDraftStandardQuestion,
        },
        {
          content: '',
          role: 'Assistent',
          chatCorrelationId,
          isPreparingReply: true,
          preparationStatusMessage: '',
          isDraftStandardQuestion,
          previewModelName: activePreview?.displayName,
        },
      ]);

    try {
      const user = await context.userManager?.getUser();
      const response = assistantType === AssistantType.Fasit ? await getPrompt(prompt, user?.access_token) : await getCitizenPrompt(prompt, user?.access_token);

      const refusalCode = response && !response.ok && assistantType === AssistantType.Citizen ? await readSummaPreviewRefusalCode(response) : undefined;
      if (refusalCode) {
        // A question about one citizen must never be handed back into another citizen's chat.
        if (messagesScope.current === askedInScope) {
          handBackRefusedQuestion(refusalCode, prompt.content || '', chatCorrelationId);
        } else {
          setIsLoading(false);
        }
        return;
      }

      if (!response?.body || !response.ok) {
        throw new Error();
      }

      const reader = response.body.getReader();
      await readStream(reader);
    } catch (errorMessage) {
      setIsLoading(false);
      failPendingReply();
    }
  };

  const readStream = async (reader: ReadableStreamDefaultReader<Uint8Array>) => {
    const decoder = new TextDecoder();
    let buffer = '';
    let answerReceived = false;

    const processChunk = ({ done, value }: ReadableStreamReadResult<Uint8Array>) => {
      if (done) {
        setIsLoading(false);
        if (!answerReceived) {
          failPendingReply();
        }
        return;
      }

      const chunkText = decoder.decode(value, { stream: true });
      buffer += chunkText;

      const { remainder, receivedAssistantContent } = processBuffer(buffer);
      buffer = remainder;
      answerReceived = answerReceived || receivedAssistantContent;

      watchStreamErrors(reader.read().then(processChunk));
    };

    watchStreamErrors(reader.read().then(processChunk));
  };

  function watchStreamErrors(chunkProcessing: Promise<void>) {
    chunkProcessing.catch(() => {
      setIsLoading(false);
      failPendingReply();
    });
  }

  // A refusal is not an answer, so the question is handed back, and the next one starts a conversation on the other model.
  // The stored chat goes too, or a remount would bring back the preview and the refused question.
  function handBackRefusedQuestion(systemCode: SummaPreviewRefusalCode, question: string, chatCorrelationId: string) {
    setIsLoading(false);
    setMessages(prevMessages => prevMessages.filter(message => message.chatCorrelationId !== chatCorrelationId));
    setSessionId(undefined);
    setPreview(undefined);
    setPreviewRefusal({ systemCode, question });
    sessionStorage.removeItem(chatMessageStorageKey);
  }

  function failPendingReply() {
    setMessages(prevMessages => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage?.role !== 'Assistent') {
        return prevMessages;
      }

      const content = lastMessage.content ? `${lastMessage.content}\n\n${unfinishedReplyText}` : unfinishedReplyText;
      return [...prevMessages.slice(0, -1), { ...lastMessage, content, isPreparingReply: false }];
    });
  }

  const processBuffer = (buffer: string): { remainder: string; receivedAssistantContent: boolean } => {
    const lines = buffer.split('\n');
    const lastLine = lines.pop() ?? ''; // Keep the last incomplete part in buffer

    const newChatMessages = lines.filter(line => line.trim()).map(parseJSONLine);

    // Find the last message with role 'Tool' or 'Function' to update the preparation status message
    const toolUpdate = newChatMessages
      .slice()
      .reverse()
      .find(msg => msg?.role === 'Tool' || msg?.role === 'Function');

    // If assistant content is received, update the progress message
    const assistantContent = newChatMessages
      .filter(x => x?.role === 'Assistent' || x?.role === undefined)
      .map(msg => msg?.content)
      .join('');

    const citations = newChatMessages.find(xy => xy?.citations && xy?.citations?.length > 0)?.citations;
    const concatMessage = {
      content: assistantContent,
      Role: 'Assistent',
      citations,
      chatCorrelationId: newChatMessages[0]?.chatCorrelationId,
      isPreparingReply: assistantContent ? false : true, // isPreparingReply is false once we start receiving assistant messages
      preparationStatusMessage: toolUpdate?.content,
    };
    updateLastMessageContentAndCitations(concatMessage);
    return { remainder: lastLine, receivedAssistantContent: assistantContent.length > 0 };
  };

  const parseJSONLine = (line: string): IChatMessage | null => {
    try {
      const message = JSON.parse(line);

      // Map the integer role to the corresponding IChatMessage role
      const roleMapping: { [key: number]: IChatMessage['role'] } = {
        1: 'System',
        2: 'User',
        3: 'Assistent',
        4: 'Function',
        5: 'Tool',
      };

      return {
        content: message.Content,
        userName: 'Fasit assistent',
        role: roleMapping[message.Role] || undefined,
        citations: message.Citations?.map(
          (citation: any) =>
            ({
              id: citation.Id,
              link: citation.Link,
              title: citation.Title,
              text: citation.Text,
              CitationSourceType: citation.CitationContentType,
              isAlternativeResult: citation.IsAlternative,
            } as ICitation)
        ),
      } as IChatMessage;
    } catch (errorMessage) {
      setError('Der skete en fejl under læsning af data');
      console.error('Error parsing JSON:', errorMessage, line);
      return null;
    }
  };

  const updateLastMessageContentAndCitations = (message: IChatMessage) => {
    setMessages(prevMessages => {
      if (prevMessages.length === 0) {
        return [message];
      }

      const lastMessage = { ...prevMessages[prevMessages.length - 1] };
      lastMessage.content += message.content;
      lastMessage.userName = 'Fasit assistent';
      lastMessage.timestamp = formatDateTime(new Date().toISOString());
      if (message.citations) {
        lastMessage.citations = message.citations;
      }

      // Update with message progress message and loading state
      lastMessage.isPreparingReply = message.isPreparingReply;
      lastMessage.preparationStatusMessage = message.preparationStatusMessage;

      return [...prevMessages.slice(0, -1), lastMessage];
    });
  };

  const updateMessageFeedback = (chatFeedbackMsg: IChatMessage) => {
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg.chatCorrelationId === chatFeedbackMsg.chatCorrelationId && msg.role === 'Assistent'
          ? { ...msg, qualityComment: chatFeedbackMsg.qualityComment, vote: chatFeedbackMsg.vote }
          : msg
      )
    );
  };
  const setChatFeedback = async (chatFeedbackMsg: IChatMessage) => {
    const correlatedMessages = messages.filter(msg => msg.chatCorrelationId === chatFeedbackMsg.chatCorrelationId);

    if (correlatedMessages.length === 2) {
      const userMessage = correlatedMessages.find(msg => msg.role === 'User');
      let assistantMessage = correlatedMessages.find(msg => msg.role === 'Assistent');

      if (!userMessage || !assistantMessage) {
        setError('Der skete en fejl');
        return;
      }

      assistantMessage = { ...assistantMessage, qualityComment: chatFeedbackMsg.qualityComment, vote: chatFeedbackMsg.vote };

      const dto: CreateUserChatMessageDto = {
        id: assistantMessage.chatCorrelationId,
        qualityComment: assistantMessage.qualityComment,
        question: userMessage.content,
        response: assistantMessage.content,
        vote: assistantMessage.vote,
      };

      const client = moduleClientFactory.createAiSearchClient();

      await client.createOrUpdateUserChatMessage({ userChatMessageDto: dto });

      updateMessageFeedback(assistantMessage);
    } else {
      setError('Der skete en fejl');
    }
  };

  return {
    messages,
    isLoading,
    handlePrompt,
    error,
    clearChat,
    setChatFeedback,
    citationsTypes,
    setCitationsTypes,
    preview: activePreview,
    previewRefusal,
    startNewConversation,
  };
};
